import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PetAvatar from '../components/PetAvatar';
import { 
  SparklesIcon, 
  HeartIcon, 
  AcademicCapIcon,
  ChatBubbleBottomCenterTextIcon,
  ArrowRightIcon,
  StarIcon,
  PlayCircleIcon,
  UserGroupIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';

const Homepage = () => {
  const { user } = useAuth();

  const features = [
    {
      icon: AcademicCapIcon,
      title: "Smart Training Programs",
      description: "AI-powered training plans tailored to your pet's personality and learning style",
      color: "from-primary-500 to-primary-600",
      emoji: "🎓"
    },
    {
      icon: ChatBubbleBottomCenterTextIcon,
      title: "Virtual Vet Consultant",
      description: "Get expert advice and answers to your pet care questions anytime",
      color: "from-secondary-500 to-secondary-600",
      emoji: "🩺"
    },
    {
      icon: TrophyIcon,
      title: "Progress Tracking",
      description: "Monitor your pet's learning journey with detailed progress insights",
      color: "from-accent-500 to-accent-600",
      emoji: "🏆"
    },
    {
      icon: UserGroupIcon,
      title: "Community Support",
      description: "Connect with other pet parents and share training experiences",
      color: "from-purple-500 to-purple-600",
      emoji: "👥"
    }
  ];

  const testimonials = [
    {
      name: "Sarah & Max",
      petType: "Dog",
      text: "Max learned to sit, stay, and roll over in just 2 weeks! The AI recommendations were spot-on.",
      rating: 5
    },
    {
      name: "Mike & Whiskers",
      petType: "Cat",
      text: "I never thought I could train my cat, but this app made it fun and achievable!",
      rating: 5
    },
    {
      name: "Emma & Buddy",
      petType: "Dog",
      text: "The virtual vet feature saved me multiple trips to the clinic. Absolutely love it!",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="relative z-10 pb-8 sm:pb-16 md:pb-20 lg:pb-28 xl:pb-32">
            {/* Navigation */}
            <nav className="relative flex items-center justify-between pt-6 px-4 sm:px-6 lg:px-8">
              <div className="flex items-center space-x-3">
                <div className="animate-bounce-slow">
                  <SparklesIcon className="h-8 w-8 text-primary-500" />
                </div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                  Jahnvar
                </h1>
              </div>
              
              <div className="flex items-center space-x-4">
                {user ? (
                  <div className="flex items-center space-x-4">
                    <span className="text-gray-700">Welcome back, {user.firstName}!</span>
                    <Link
                      to="/app/dashboard"
                      className="btn btn-primary"
                    >
                      Go to Dashboard
                    </Link>
                  </div>
                ) : (
                  <div className="flex items-center space-x-4">
                    <Link
                      to="/login"
                      className="text-gray-700 hover:text-primary-600 font-medium transition-colors"
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/register"
                      className="btn btn-primary"
                    >
                      Get Started
                    </Link>
                  </div>
                )}
              </div>
            </nav>

            {/* Hero Content */}
            <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div className="text-center lg:text-left">
                  <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
                    <span className="block">Welcome to</span>
                    <span className="block bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                      Jahnvar 🐾
                    </span>
                  </h1>
                  
                  <p className="mt-3 text-base text-gray-500 sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-5 md:text-xl lg:mx-0">
                    Your AI-powered companion for pet training and care. Transform your furry friend into the best-behaved pet 
                    on the block with personalized training programs and expert guidance.
                  </p>
                  
                  <div className="mt-5 sm:mt-8 sm:flex sm:justify-center lg:justify-start">
                    <div className="rounded-md shadow">
                      <Link
                        to={user ? "/app/dashboard" : "/register"}
                        className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 transform hover:scale-105 transition-all duration-200 shadow-lg md:py-4 md:text-lg md:px-10"
                      >
                        <PlayCircleIcon className="h-5 w-5 mr-2" />
                        {user ? "Continue Training" : "Start Training Today"}
                        <ArrowRightIcon className="h-5 w-5 ml-2" />
                      </Link>
                    </div>
                    <div className="mt-3 sm:mt-0 sm:ml-3">
                      <a
                        href="#features"
                        className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-xl text-primary-700 bg-primary-100 hover:bg-primary-200 transform hover:scale-105 transition-all duration-200 md:py-4 md:text-lg md:px-10"
                      >
                        Learn More
                      </a>
                    </div>
                  </div>
                </div>

                {/* Hero Animation */}
                <div className="relative">
                  <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto">
                    <div className="animate-float" style={{ animationDelay: '0s' }}>
                      <div className="bg-white p-6 rounded-3xl shadow-lg border border-gray-100 transform rotate-3 hover:rotate-6 transition-transform">
                        <PetAvatar petType="Dog" size="xl" animated={true} />
                        <p className="mt-2 text-center font-medium text-gray-900">Buddy</p>
                        <p className="text-center text-sm text-green-600">✓ Sit Mastered!</p>
                      </div>
                    </div>
                    
                    <div className="animate-float" style={{ animationDelay: '0.5s' }}>
                      <div className="bg-white p-6 rounded-3xl shadow-lg border border-gray-100 transform -rotate-2 hover:-rotate-6 transition-transform">
                        <PetAvatar petType="Cat" size="xl" animated={true} />
                        <p className="mt-2 text-center font-medium text-gray-900">Whiskers</p>
                        <p className="text-center text-sm text-blue-600">🎯 High Five Pro!</p>
                      </div>
                    </div>
                    
                    <div className="animate-float" style={{ animationDelay: '1s' }}>
                      <div className="bg-white p-6 rounded-3xl shadow-lg border border-gray-100 transform rotate-1 hover:rotate-4 transition-transform">
                        <PetAvatar petType="Bird" size="xl" animated={true} />
                        <p className="mt-2 text-center font-medium text-gray-900">Tweety</p>
                        <p className="text-center text-sm text-purple-600">🗣️ Talking Champion!</p>
                      </div>
                    </div>
                    
                    <div className="animate-float" style={{ animationDelay: '1.5s' }}>
                      <div className="bg-white p-6 rounded-3xl shadow-lg border border-gray-100 transform -rotate-3 hover:-rotate-6 transition-transform">
                        <PetAvatar petType="Rabbit" size="xl" animated={true} />
                        <p className="mt-2 text-center font-medium text-gray-900">Hoppy</p>
                        <p className="text-center text-sm text-orange-600">🏃 Agility Star!</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Floating elements */}
                  <div className="absolute -top-4 -left-4 animate-spin-slow">
                    <SparklesIcon className="h-8 w-8 text-accent-400" />
                  </div>
                  <div className="absolute -bottom-4 -right-4 animate-bounce-slow">
                    <HeartIcon className="h-8 w-8 text-secondary-400" />
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Everything Your Pet Needs to Succeed 🌟
            </h2>
            <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
              Our comprehensive platform combines cutting-edge AI with proven training methods 
              to give your pet the best learning experience possible.
            </p>
          </div>

          <div className="mt-20 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="relative bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl hover:scale-105 transform transition-all duration-300"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="absolute -top-4 left-6">
                  <div className={`bg-gradient-to-r ${feature.color} p-3 rounded-xl shadow-lg`}>
                    <feature.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
                
                <div className="pt-8">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center">
                    <span className="mr-2 text-2xl">{feature.emoji}</span>
                    {feature.title}
                  </h3>
                  <p className="mt-3 text-gray-600 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-r from-primary-50 to-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Happy Pets, Happier Parents! 😊
            </h2>
            <p className="mt-4 text-xl text-gray-600">
              See what our community has achieved together
            </p>
          </div>

          <div className="mt-20 grid grid-cols-1 gap-8 md:grid-cols-3">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.name}
                className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="flex items-center mb-4">
                  <PetAvatar petType={testimonial.petType} size="md" />
                  <div className="ml-4">
                    <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                    <div className="flex items-center">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <StarIcon key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-gray-600 italic">"{testimonial.text}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-secondary-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
            Ready to Start Your Pet's Training Journey? 🚀
          </h2>
          <p className="mt-4 text-xl text-primary-100">
            Join thousands of pet parents who have transformed their furry friends into well-behaved companions!
          </p>
          
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={user ? "/app/dashboard" : "/register"}
              className="px-8 py-4 bg-white text-primary-600 font-bold rounded-xl hover:bg-gray-50 transform hover:scale-105 transition-all duration-200 shadow-lg"
            >
              <span className="flex items-center justify-center">
                <SparklesIcon className="h-5 w-5 mr-2" />
                {user ? "Continue Your Journey" : "Start Free Today"}
                <ArrowRightIcon className="h-5 w-5 ml-2" />
              </span>
            </Link>
            
            {!user && (
              <Link
                to="/login"
                className="px-8 py-4 border-2 border-white text-white font-bold rounded-xl hover:bg-white hover:text-primary-600 transform hover:scale-105 transition-all duration-200"
              >
                I Already Have an Account
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <SparklesIcon className="h-6 w-6 text-primary-400" />
              <span className="text-lg font-bold">Jahnvar</span>
            </div>
            <p className="text-gray-400">
              © 2024 Jahnvar. Made with ❤️ for pet parents everywhere.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Homepage;